/**
 * GlassHeader Component - Semantic Silo Navigation
 * 3 main mega-menus: BPO & Call Center, IT & Development, Industries
 * Glassmorphism sticky header with full i18n support
 */
import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { startHref } from '@/lib/ctaSource';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Menu, X, ChevronDown, ChevronRight, Globe, LogIn, User, LogOut, LayoutDashboard, 
  Headphones, Code, ShoppingCart, Lightbulb, Shield, Building2, Users, Phone,
  MessageSquare, BarChart3, Wrench, TrendingUp, Heart, BookOpen, Target,
  Settings, GraduationCap, FileText, Sparkles, Smartphone, Cloud, Database,
  Lock, Zap, MapPin, Briefcase, CreditCard, Stethoscope
} from 'lucide-react';
import { Logo } from '@/components/ui/Logo';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';
import { useLanguage } from '@/contexts/LanguageContextNew';
import { languages, SupportedLanguage } from '@/lib/i18n-new';
import { supabase } from '@/integrations/supabase/client';
import { User as SupabaseUser } from '@supabase/supabase-js';

/* ───────────────────── Localized Labels ───────────────────── */
const getSiloLabels = (language: SupportedLanguage) => {
  const m: Record<string, Record<string, string>> = {
    en: {
      home: 'Home', services: 'Services', blog: 'Blog', bpoSilo: 'BPO & Call Center', itSilo: 'IT & Development', industries: 'Industries',
      pricing: 'Pricing', about: 'About', contact: 'Contact', getQuote: 'Request a Quote',
      // BPO columns
      omnichannel: 'Omnichannel Support', contentMod: 'Content Moderation', leadGen: 'Lead Generation', backOffice: 'Back-Office & Data Entry',
      inbound: 'Inbound Customer Care', technical: 'Technical Support', livechat: 'Live Chat & Messaging', social: 'Social Media Care',
      moderation: 'Trust & Safety', contentReview: 'Content Review', fraudDetection: 'Fraud Detection', compliance: 'Compliance Monitoring',
      outbound: 'Outbound Sales', sdr: 'SDR & Appointment Setting', leadQual: 'Lead Qualification', retention: 'Retention & Win-back',
      orderMgmt: 'Order Management', billing: 'Billing & Collections', dataEntry: 'Data Entry & Processing', virtualAssistant: 'Virtual Assistant',
      viewAll: 'View all →', caseStudies: 'Case Studies', locations: 'Locations', trustCenter: 'Trust Center',
      careers: 'Careers', resources: 'Resources',
      // IT columns
      staffAug: 'IT Staff Augmentation', customSoftware: 'Custom Enterprise Software', cloudDevops: 'Cloud & DevOps', cybersecurity: 'Cybersecurity',
      saas: 'Custom SaaS Development', portals: 'Enterprise Portals', mobile: 'Mobile Apps', ecommerce: 'E-commerce Platforms',
      uiux: 'UI/UX Design', aiml: 'AI/ML Development', dataEng: 'Data Engineering & BI', legacy: 'Legacy Modernization',
      cloud: 'Cloud Migration', devops: 'DevOps Automation', managed: 'Managed Services', iot: 'IoT Development',
      appSec: 'Application Security', penTest: 'Penetration Testing', soc: 'SOC as a Service', blockchain: 'Blockchain & Web3',
      // Industries
      healthcare: 'Healthcare', fintech: 'FinTech & Banking', ecommerceInd: 'E-commerce & Retail',
      telecom: 'Telecom & Media', travel: 'Travel & Hospitality', insurance: 'Insurance',
      education: 'Education & EdTech', realestate: 'Real Estate', logistics: 'Logistics & Supply Chain',
      energy: 'Energy & Utilities', government: 'Government', gaming: 'Gaming & Entertainment',
      morocco: 'Morocco (HQ)', spain: 'Spain',
      configureQuote: 'Configure your solution',
    },
    fr: {
      home: 'Accueil', services: 'Services', blog: 'Blog', bpoSilo: 'BPO & Centre d\'Appels', itSilo: 'IT & Développement', industries: 'Industries',
      pricing: 'Tarifs', about: 'À propos', contact: 'Contact', getQuote: 'Obtenir un devis',
      omnichannel: 'Support Omnicanal', contentMod: 'Modération de Contenu', leadGen: 'Génération de Leads', backOffice: 'Back-Office & Saisie',
      inbound: 'Support Client Entrant', technical: 'Support Technique', livechat: 'Chat & Messagerie', social: 'Support Réseaux Sociaux',
      moderation: 'Trust & Safety', contentReview: 'Revue de Contenu', fraudDetection: 'Détection de Fraude', compliance: 'Suivi de Conformité',
      outbound: 'Ventes Sortantes', sdr: 'SDR & Prise de RDV', leadQual: 'Qualification de Leads', retention: 'Fidélisation & Réactivation',
      orderMgmt: 'Gestion des Commandes', billing: 'Facturation & Recouvrement', dataEntry: 'Saisie & Traitement', virtualAssistant: 'Assistant Virtuel',
      viewAll: 'Voir tout →', caseStudies: 'Études de cas', locations: 'Emplacements', trustCenter: 'Centre de confiance',
      careers: 'Carrières', resources: 'Ressources',
      staffAug: 'Renfort d\'Équipe IT', customSoftware: 'Logiciels Entreprise', cloudDevops: 'Cloud & DevOps', cybersecurity: 'Cybersécurité',
      saas: 'Développement SaaS', portals: 'Portails Entreprise', mobile: 'Applications Mobiles', ecommerce: 'Plateformes E-commerce',
      uiux: 'Design UI/UX', aiml: 'Développement IA/ML', dataEng: 'Data & BI', legacy: 'Modernisation Legacy',
      cloud: 'Migration Cloud', devops: 'Automatisation DevOps', managed: 'Services Managés', iot: 'Développement IoT',
      appSec: 'Sécurité Applicative', penTest: 'Tests d\'Intrusion', soc: 'SOC as a Service', blockchain: 'Blockchain & Web3',
      healthcare: 'Santé', fintech: 'FinTech & Banque', ecommerceInd: 'E-commerce & Retail',
      telecom: 'Télécom & Média', travel: 'Voyage & Hôtellerie', insurance: 'Assurance',
      education: 'Éducation & EdTech', realestate: 'Immobilier', logistics: 'Logistique',
      energy: 'Énergie & Utilities', government: 'Gouvernement', gaming: 'Jeux & Divertissement',
      morocco: 'Maroc (Siège)', spain: 'Espagne',
      configureQuote: 'Configurez votre solution',
    },
    es: {
      home: 'Inicio', services: 'Servicios', blog: 'Blog', bpoSilo: 'BPO & Call Center', itSilo: 'IT & Desarrollo', industries: 'Industrias',
      pricing: 'Precios', about: 'Nosotros', contact: 'Contacto', getQuote: 'Obtener presupuesto',
      omnichannel: 'Soporte Omnicanal', contentMod: 'Moderación de Contenido', leadGen: 'Generación de Leads', backOffice: 'Back-Office & Datos',
      inbound: 'Atención al Cliente', technical: 'Soporte Técnico', livechat: 'Chat en Vivo', social: 'Atención en Redes',
      moderation: 'Confianza y Seguridad', contentReview: 'Revisión de Contenido', fraudDetection: 'Detección de Fraude', compliance: 'Monitoreo de Cumplimiento',
      outbound: 'Ventas Salientes', sdr: 'SDR y Citas', leadQual: 'Calificación de Leads', retention: 'Retención & Win-back',
      orderMgmt: 'Gestión de Pedidos', billing: 'Facturación y Cobros', dataEntry: 'Entrada de Datos', virtualAssistant: 'Asistente Virtual',
      viewAll: 'Ver todo →', caseStudies: 'Casos de éxito', locations: 'Ubicaciones', trustCenter: 'Centro de confianza',
      careers: 'Carreras', resources: 'Recursos',
      staffAug: 'Staff Augmentation', customSoftware: 'Software Empresarial', cloudDevops: 'Cloud & DevOps', cybersecurity: 'Ciberseguridad',
      saas: 'Desarrollo SaaS', portals: 'Portales Empresariales', mobile: 'Apps Móviles', ecommerce: 'Plataformas E-commerce',
      uiux: 'Diseño UI/UX', aiml: 'Desarrollo IA/ML', dataEng: 'Data & BI', legacy: 'Modernización Legacy',
      cloud: 'Migración Cloud', devops: 'Automatización DevOps', managed: 'Servicios Gestionados', iot: 'Desarrollo IoT',
      appSec: 'Seguridad Aplicativa', penTest: 'Pentesting', soc: 'SOC as a Service', blockchain: 'Blockchain & Web3',
      healthcare: 'Salud', fintech: 'FinTech & Banca', ecommerceInd: 'E-commerce & Retail',
      telecom: 'Telecom & Media', travel: 'Viajes & Hotelería', insurance: 'Seguros',
      education: 'Educación & EdTech', realestate: 'Inmobiliaria', logistics: 'Logística',
      energy: 'Energía', government: 'Gobierno', gaming: 'Gaming & Entretenimiento',
      morocco: 'Marruecos (Sede)', spain: 'España',
      configureQuote: 'Configura tu solución',
    },
    ar: {
      home: 'الرئيسية', services: 'الخدمات', blog: 'المدونة', bpoSilo: 'BPO ومركز الاتصال', itSilo: 'تكنولوجيا المعلومات والتطوير', industries: 'القطاعات',
      pricing: 'الأسعار', about: 'عن الشركة', contact: 'اتصل بنا', getQuote: 'احصل على عرض سعر',
      omnichannel: 'دعم متعدد القنوات', contentMod: 'إشراف على المحتوى', leadGen: 'توليد العملاء المحتملين', backOffice: 'مكتب خلفي وإدخال بيانات',
      inbound: 'خدمة العملاء الواردة', technical: 'الدعم الفني', livechat: 'الدردشة المباشرة', social: 'رعاية وسائل التواصل',
      moderation: 'الثقة والسلامة', contentReview: 'مراجعة المحتوى', fraudDetection: 'كشف الاحتيال', compliance: 'مراقبة الامتثال',
      outbound: 'المبيعات الصادرة', sdr: 'SDR وحجز المواعيد', leadQual: 'تأهيل العملاء المحتملين', retention: 'الاحتفاظ والاسترداد',
      orderMgmt: 'إدارة الطلبات', billing: 'الفواتير والتحصيل', dataEntry: 'إدخال ومعالجة البيانات', virtualAssistant: 'مساعد افتراضي',
      viewAll: '← عرض الكل', caseStudies: 'دراسات الحالة', locations: 'المواقع', trustCenter: 'مركز الثقة',
      careers: 'الوظائف', resources: 'الموارد',
      staffAug: 'تعزيز فريق تكنولوجيا المعلومات', customSoftware: 'برمجيات مؤسسية مخصصة', cloudDevops: 'السحابة و DevOps', cybersecurity: 'الأمن السيبراني',
      saas: 'تطوير SaaS', portals: 'بوابات المؤسسات', mobile: 'تطبيقات الجوال', ecommerce: 'منصات التجارة الإلكترونية',
      uiux: 'تصميم UI/UX', aiml: 'تطوير الذكاء الاصطناعي', dataEng: 'هندسة البيانات', legacy: 'تحديث الأنظمة القديمة',
      cloud: 'ترحيل سحابي', devops: 'أتمتة DevOps', managed: 'خدمات مدارة', iot: 'تطوير إنترنت الأشياء',
      appSec: 'أمان التطبيقات', penTest: 'اختبار الاختراق', soc: 'SOC كخدمة', blockchain: 'بلوكتشين و Web3',
      healthcare: 'الرعاية الصحية', fintech: 'التكنولوجيا المالية والبنوك', ecommerceInd: 'التجارة الإلكترونية',
      telecom: 'الاتصالات والإعلام', travel: 'السفر والضيافة', insurance: 'التأمين',
      education: 'التعليم', realestate: 'العقارات', logistics: 'اللوجستيات',
      energy: 'الطاقة', government: 'الحكومة', gaming: 'الألعاب والترفيه',
      morocco: 'المغرب (المقر)', spain: 'إسبانيا',
      configureQuote: 'قم بتكوين الحل الخاص بك',
    },
    de: {
      home: 'Startseite', services: 'Dienstleistungen', blog: 'Blog', bpoSilo: 'BPO & Call Center', itSilo: 'IT & Entwicklung', industries: 'Branchen',
      pricing: 'Preise', about: 'Über uns', contact: 'Kontakt', getQuote: 'Angebot anfordern',
      omnichannel: 'Omnichannel-Support', contentMod: 'Content-Moderation', leadGen: 'Lead-Generierung', backOffice: 'Back-Office & Dateneingabe',
      inbound: 'Inbound-Kundenservice', technical: 'Technischer Support', livechat: 'Live-Chat & Messaging', social: 'Social-Media-Support',
      moderation: 'Trust & Safety', contentReview: 'Content-Überprüfung', fraudDetection: 'Betrugserkennung', compliance: 'Compliance-Überwachung',
      outbound: 'Outbound-Vertrieb', sdr: 'SDR & Terminvereinbarung', leadQual: 'Lead-Qualifizierung', retention: 'Retention & Win-back',
      orderMgmt: 'Auftragsmanagement', billing: 'Abrechnung & Inkasso', dataEntry: 'Dateneingabe & -verarbeitung', virtualAssistant: 'Virtueller Assistent',
      viewAll: 'Alle ansehen →', caseStudies: 'Fallstudien', locations: 'Standorte', trustCenter: 'Trust Center',
      careers: 'Karriere', resources: 'Ressourcen',
      staffAug: 'IT Staff Augmentation', customSoftware: 'Individuelle Unternehmenssoftware', cloudDevops: 'Cloud & DevOps', cybersecurity: 'Cybersicherheit',
      saas: 'Individuelle SaaS-Entwicklung', portals: 'Enterprise-Portale', mobile: 'Mobile Apps', ecommerce: 'E-Commerce-Plattformen',
      uiux: 'UI/UX Design', aiml: 'KI/ML-Entwicklung', dataEng: 'Data Engineering & BI', legacy: 'Legacy-Modernisierung',
      cloud: 'Cloud-Migration', devops: 'DevOps-Automatisierung', managed: 'Managed Services', iot: 'IoT-Entwicklung',
      appSec: 'Anwendungssicherheit', penTest: 'Penetrationstests', soc: 'SOC as a Service', blockchain: 'Blockchain & Web3',
      healthcare: 'Gesundheitswesen', fintech: 'FinTech & Banking', ecommerceInd: 'E-Commerce & Handel',
      telecom: 'Telekommunikation & Medien', travel: 'Reise & Gastgewerbe', insurance: 'Versicherung',
      education: 'Bildung & EdTech', realestate: 'Immobilien', logistics: 'Logistik & Lieferkette',
      energy: 'Energie & Versorgung', government: 'Regierung', gaming: 'Gaming & Unterhaltung',
      morocco: 'Marokko (Hauptsitz)', spain: 'Spanien',
      configureQuote: 'Konfigurieren Sie Ihre Lösung',
    },
    it: {
      home: 'Home', services: 'Servizi', blog: 'Blog', bpoSilo: 'BPO & Call Center', itSilo: 'IT & Sviluppo', industries: 'Settori',
      pricing: 'Prezzi', about: 'Chi Siamo', contact: 'Contatti', getQuote: 'Richiedi un preventivo',
      omnichannel: 'Supporto Omnicanale', contentMod: 'Moderazione Contenuti', leadGen: 'Lead Generation', backOffice: 'Back-Office & Data Entry',
      inbound: 'Assistenza Clienti Inbound', technical: 'Supporto Tecnico', livechat: 'Live Chat & Messaggistica', social: 'Social Media Care',
      moderation: 'Trust & Safety', contentReview: 'Revisione Contenuti', fraudDetection: 'Rilevamento Frodi', compliance: 'Monitoraggio Conformità',
      outbound: 'Vendite Outbound', sdr: 'SDR & Appuntamenti', leadQual: 'Qualificazione Lead', retention: 'Fidelizzazione & Win-back',
      orderMgmt: 'Gestione Ordini', billing: 'Fatturazione & Recupero Crediti', dataEntry: 'Data Entry & Elaborazione', virtualAssistant: 'Assistente Virtuale',
      viewAll: 'Vedi tutto →', caseStudies: 'Case Study', locations: 'Sedi', trustCenter: 'Centro Fiducia',
      careers: 'Carriere', resources: 'Risorse',
      staffAug: 'Staff Augmentation IT', customSoftware: 'Software Aziendale', cloudDevops: 'Cloud & DevOps', cybersecurity: 'Cybersicurezza',
      saas: 'Sviluppo SaaS', portals: 'Portali Aziendali', mobile: 'App Mobile', ecommerce: 'Piattaforme E-commerce',
      uiux: 'Design UI/UX', aiml: 'Sviluppo AI/ML', dataEng: 'Data Engineering & BI', legacy: 'Modernizzazione Legacy',
      cloud: 'Migrazione Cloud', devops: 'Automazione DevOps', managed: 'Servizi Gestiti', iot: 'Sviluppo IoT',
      appSec: 'Sicurezza Applicativa', penTest: 'Penetration Testing', soc: 'SOC as a Service', blockchain: 'Blockchain & Web3',
      healthcare: 'Sanità', fintech: 'FinTech & Banche', ecommerceInd: 'E-commerce & Retail',
      telecom: 'Telecom & Media', travel: 'Viaggi & Ospitalità', insurance: 'Assicurazioni',
      education: 'Istruzione & EdTech', realestate: 'Immobiliare', logistics: 'Logistica & Supply Chain',
      energy: 'Energia & Utilities', government: 'Governo', gaming: 'Gaming & Intrattenimento',
      morocco: 'Marocco (Sede)', spain: 'Spagna',
      configureQuote: 'Configura la tua soluzione',
    },
    pt: {
      home: 'Início', services: 'Serviços', blog: 'Blog', bpoSilo: 'BPO & Call Center', itSilo: 'TI & Desenvolvimento', industries: 'Indústrias',
      pricing: 'Preços', about: 'Sobre', contact: 'Contacto', getQuote: 'Solicitar orçamento',
      omnichannel: 'Suporte Omnicanal', contentMod: 'Moderação de Conteúdo', leadGen: 'Geração de Leads', backOffice: 'Back-Office & Entrada de Dados',
      inbound: 'Atendimento ao Cliente', technical: 'Suporte Técnico', livechat: 'Chat ao Vivo', social: 'Atendimento em Redes Sociais',
      moderation: 'Trust & Safety', contentReview: 'Revisão de Conteúdo', fraudDetection: 'Deteção de Fraude', compliance: 'Monitoramento de Conformidade',
      outbound: 'Vendas Outbound', sdr: 'SDR & Agendamento', leadQual: 'Qualificação de Leads', retention: 'Retenção & Win-back',
      orderMgmt: 'Gestão de Pedidos', billing: 'Faturação & Cobranças', dataEntry: 'Entrada & Processamento de Dados', virtualAssistant: 'Assistente Virtual',
      viewAll: 'Ver tudo →', caseStudies: 'Estudos de Caso', locations: 'Localizações', trustCenter: 'Centro de Confiança',
      careers: 'Carreiras', resources: 'Recursos',
      staffAug: 'Staff Augmentation TI', customSoftware: 'Software Empresarial', cloudDevops: 'Cloud & DevOps', cybersecurity: 'Cibersegurança',
      saas: 'Desenvolvimento SaaS', portals: 'Portais Empresariais', mobile: 'Apps Mobile', ecommerce: 'Plataformas E-commerce',
      uiux: 'Design UI/UX', aiml: 'Desenvolvimento IA/ML', dataEng: 'Data Engineering & BI', legacy: 'Modernização Legacy',
      cloud: 'Migração Cloud', devops: 'Automação DevOps', managed: 'Serviços Gerenciados', iot: 'Desenvolvimento IoT',
      appSec: 'Segurança Aplicativa', penTest: 'Testes de Penetração', soc: 'SOC as a Service', blockchain: 'Blockchain & Web3',
      healthcare: 'Saúde', fintech: 'FinTech & Banca', ecommerceInd: 'E-commerce & Retail',
      telecom: 'Telecom & Media', travel: 'Viagens & Hotelaria', insurance: 'Seguros',
      education: 'Educação & EdTech', realestate: 'Imobiliário', logistics: 'Logística & Supply Chain',
      energy: 'Energia & Utilities', government: 'Governo', gaming: 'Gaming & Entretenimento',
      morocco: 'Marrocos (Sede)', spain: 'Espanha',
      configureQuote: 'Configure a sua solução',
    },
    nl: {
      home: 'Home', services: 'Diensten', blog: 'Blog', bpoSilo: 'BPO & Callcenter', itSilo: 'IT & Ontwikkeling', industries: 'Sectoren',
      pricing: 'Prijzen', about: 'Over ons', contact: 'Contact', getQuote: 'Offerte aanvragen',
      omnichannel: 'Omnichannel-support', contentMod: 'Contentmoderatie', leadGen: 'Leadgeneratie', backOffice: 'Back-Office & Data-invoer',
      inbound: 'Inbound Klantenservice', technical: 'Technische Ondersteuning', livechat: 'Live Chat & Messaging', social: 'Social Media Zorg',
      moderation: 'Trust & Safety', contentReview: 'Contentbeoordeling', fraudDetection: 'Fraudedetectie', compliance: 'Compliancemonitoring',
      outbound: 'Outbound Sales', sdr: 'SDR & Afspraken', leadQual: 'Leadkwalificatie', retention: 'Retentie & Win-back',
      orderMgmt: 'Orderbeheer', billing: 'Facturatie & Incasso', dataEntry: 'Data-invoer & -verwerking', virtualAssistant: 'Virtuele Assistent',
      viewAll: 'Alles bekijken →', caseStudies: 'Cases', locations: 'Locaties', trustCenter: 'Vertrouwenscentrum',
      careers: 'Carrières', resources: 'Bronnen',
      staffAug: 'IT Staff Augmentation', customSoftware: 'Maatwerk Bedrijfssoftware', cloudDevops: 'Cloud & DevOps', cybersecurity: 'Cyberveiligheid',
      saas: 'Maatwerk SaaS-ontwikkeling', portals: 'Enterprise Portalen', mobile: 'Mobiele Apps', ecommerce: 'E-commerceplatformen',
      uiux: 'UI/UX Design', aiml: 'AI/ML-ontwikkeling', dataEng: 'Data Engineering & BI', legacy: 'Legacy-modernisering',
      cloud: 'Cloudmigratie', devops: 'DevOps-automatisering', managed: 'Managed Services', iot: 'IoT-ontwikkeling',
      appSec: 'Applicatiebeveiliging', penTest: 'Penetratietesten', soc: 'SOC as a Service', blockchain: 'Blockchain & Web3',
      healthcare: 'Gezondheidszorg', fintech: 'FinTech & Bankwezen', ecommerceInd: 'E-commerce & Retail',
      telecom: 'Telecom & Media', travel: 'Reizen & Horeca', insurance: 'Verzekeringen',
      education: 'Onderwijs & EdTech', realestate: 'Vastgoed', logistics: 'Logistiek & Supply Chain',
      energy: 'Energie & Utilities', government: 'Overheid', gaming: 'Gaming & Entertainment',
      morocco: 'Marokko (Hoofdkantoor)', spain: 'Spanje',
      configureQuote: 'Configureer uw oplossing',
    },
    pl: {
      home: 'Strona główna', services: 'Usługi', blog: 'Blog', bpoSilo: 'BPO & Call Center', itSilo: 'IT & Rozwój', industries: 'Branże',
      pricing: 'Cennik', about: 'O nas', contact: 'Kontakt', getQuote: 'Zapytaj o wycenę',
      omnichannel: 'Wsparcie Omnichannel', contentMod: 'Moderacja Treści', leadGen: 'Generowanie Leadów', backOffice: 'Back-Office & Wprowadzanie Danych',
      inbound: 'Obsługa Klienta', technical: 'Wsparcie Techniczne', livechat: 'Czat na Żywo', social: 'Obsługa Social Media',
      moderation: 'Trust & Safety', contentReview: 'Przegląd Treści', fraudDetection: 'Wykrywanie Oszustw', compliance: 'Monitoring Zgodności',
      outbound: 'Sprzedaż Wychodząca', sdr: 'SDR & Umawianie Spotkań', leadQual: 'Kwalifikacja Leadów', retention: 'Retencja & Win-back',
      orderMgmt: 'Zarządzanie Zamówieniami', billing: 'Fakturowanie & Windykacja', dataEntry: 'Wprowadzanie & Przetwarzanie Danych', virtualAssistant: 'Wirtualny Asystent',
      viewAll: 'Zobacz wszystko →', caseStudies: 'Studia przypadków', locations: 'Lokalizacje', trustCenter: 'Centrum Zaufania',
      careers: 'Kariera', resources: 'Zasoby',
      staffAug: 'IT Staff Augmentation', customSoftware: 'Oprogramowanie Enterprise', cloudDevops: 'Cloud & DevOps', cybersecurity: 'Cyberbezpieczeństwo',
      saas: 'Rozwój SaaS na Miarę', portals: 'Portale Enterprise', mobile: 'Aplikacje Mobilne', ecommerce: 'Platformy E-commerce',
      uiux: 'Projektowanie UI/UX', aiml: 'Rozwój AI/ML', dataEng: 'Data Engineering & BI', legacy: 'Modernizacja Legacy',
      cloud: 'Migracja do Chmury', devops: 'Automatyzacja DevOps', managed: 'Usługi Zarządzane', iot: 'Rozwój IoT',
      appSec: 'Bezpieczeństwo Aplikacji', penTest: 'Testy Penetracyjne', soc: 'SOC as a Service', blockchain: 'Blockchain & Web3',
      healthcare: 'Opieka Zdrowotna', fintech: 'FinTech & Bankowość', ecommerceInd: 'E-commerce & Retail',
      telecom: 'Telekomunikacja & Media', travel: 'Turystyka & Hotelarstwo', insurance: 'Ubezpieczenia',
      education: 'Edukacja & EdTech', realestate: 'Nieruchomości', logistics: 'Logistyka & Łańcuch Dostaw',
      energy: 'Energia & Media', government: 'Sektor Publiczny', gaming: 'Gaming & Rozrywka',
      morocco: 'Maroko (Siedziba)', spain: 'Hiszpania',
      configureQuote: 'Skonfiguruj swoje rozwiązanie',
    },
    ru: {
      home: 'Главная', services: 'Услуги', blog: 'Блог', bpoSilo: 'BPO и Колл-центр', itSilo: 'IT и Разработка', industries: 'Отрасли',
      pricing: 'Цены', about: 'О нас', contact: 'Контакты', getQuote: 'Запросить расчёт',
      omnichannel: 'Омниканальная поддержка', contentMod: 'Модерация контента', leadGen: 'Генерация лидов', backOffice: 'Бэк-офис и ввод данных',
      inbound: 'Входящая поддержка', technical: 'Техподдержка', livechat: 'Онлайн-чат', social: 'Поддержка в соцсетях',
      moderation: 'Trust & Safety', contentReview: 'Проверка контента', fraudDetection: 'Обнаружение мошенничества', compliance: 'Мониторинг соответствия',
      outbound: 'Исходящие продажи', sdr: 'SDR и назначение встреч', leadQual: 'Квалификация лидов', retention: 'Удержание и возврат',
      orderMgmt: 'Управление заказами', billing: 'Биллинг и взыскание', dataEntry: 'Ввод и обработка данных', virtualAssistant: 'Виртуальный ассистент',
      viewAll: 'Смотреть все →', caseStudies: 'Кейсы', locations: 'Локации', trustCenter: 'Центр доверия',
      careers: 'Карьера', resources: 'Ресурсы',
      staffAug: 'IT Staff Augmentation', customSoftware: 'Корпоративное ПО', cloudDevops: 'Облако и DevOps', cybersecurity: 'Кибербезопасность',
      saas: 'Разработка SaaS', portals: 'Корпоративные порталы', mobile: 'Мобильные приложения', ecommerce: 'E-commerce платформы',
      uiux: 'UI/UX Дизайн', aiml: 'Разработка ИИ/МО', dataEng: 'Инженерия данных и BI', legacy: 'Модернизация легаси',
      cloud: 'Облачная миграция', devops: 'Автоматизация DevOps', managed: 'Управляемые услуги', iot: 'Разработка IoT',
      appSec: 'Безопасность приложений', penTest: 'Пентесты', soc: 'SOC как услуга', blockchain: 'Блокчейн и Web3',
      healthcare: 'Здравоохранение', fintech: 'FinTech и банкинг', ecommerceInd: 'Электронная коммерция',
      telecom: 'Телеком и медиа', travel: 'Туризм и гостеприимство', insurance: 'Страхование',
      education: 'Образование и EdTech', realestate: 'Недвижимость', logistics: 'Логистика и цепочка поставок',
      energy: 'Энергетика', government: 'Государство', gaming: 'Игры и развлечения',
      morocco: 'Марокко (Штаб-квартира)', spain: 'Испания',
      configureQuote: 'Настройте ваше решение',
    },
    zh: {
      home: '首页', services: '服务', blog: '博客', bpoSilo: 'BPO与呼叫中心', itSilo: 'IT与开发', industries: '行业',
      pricing: '价格', about: '关于我们', contact: '联系我们', getQuote: '获取报价',
      omnichannel: '全渠道支持', contentMod: '内容审核', leadGen: '线索生成', backOffice: '后台与数据录入',
      inbound: '呼入客户服务', technical: '技术支持', livechat: '在线聊天', social: '社交媒体服务',
      moderation: '信任与安全', contentReview: '内容审查', fraudDetection: '欺诈检测', compliance: '合规监控',
      outbound: '外呼销售', sdr: 'SDR与预约', leadQual: '线索筛选', retention: '客户留存',
      orderMgmt: '订单管理', billing: '计费与催收', dataEntry: '数据录入与处理', virtualAssistant: '虚拟助理',
      viewAll: '查看全部 →', caseStudies: '案例研究', locations: '办公地点', trustCenter: '信任中心',
      careers: '招聘', resources: '资源',
      staffAug: 'IT人员扩充', customSoftware: '企业定制软件', cloudDevops: '云与DevOps', cybersecurity: '网络安全',
      saas: '定制SaaS开发', portals: '企业门户', mobile: '移动应用', ecommerce: '电商平台',
      uiux: 'UI/UX设计', aiml: 'AI/ML开发', dataEng: '数据工程与BI', legacy: '遗留系统现代化',
      cloud: '云迁移', devops: 'DevOps自动化', managed: '托管服务', iot: 'IoT开发',
      appSec: '应用安全', penTest: '渗透测试', soc: 'SOC即服务', blockchain: '区块链与Web3',
      healthcare: '医疗健康', fintech: '金融科技与银行', ecommerceInd: '电商与零售',
      telecom: '电信与媒体', travel: '旅游与酒店', insurance: '保险',
      education: '教育与EdTech', realestate: '房地产', logistics: '物流与供应链',
      energy: '能源与公用事业', government: '政府', gaming: '游戏与娱乐',
      morocco: '摩洛哥（总部）', spain: '西班牙',
      configureQuote: '配置您的解决方案',
    },
    ja: {
      home: 'ホーム', services: 'サービス', blog: 'ブログ', bpoSilo: 'BPO＆コールセンター', itSilo: 'IT＆開発', industries: '業界',
      pricing: '料金', about: '会社概要', contact: 'お問い合わせ', getQuote: 'お見積もり',
      omnichannel: 'オムニチャネルサポート', contentMod: 'コンテンツモデレーション', leadGen: 'リード獲得', backOffice: 'バックオフィス＆データ入力',
      inbound: 'インバウンドカスタマーケア', technical: 'テクニカルサポート', livechat: 'ライブチャット', social: 'SNSサポート',
      moderation: 'トラスト＆セーフティ', contentReview: 'コンテンツレビュー', fraudDetection: '不正検知', compliance: 'コンプライアンス監視',
      outbound: 'アウトバウンド営業', sdr: 'SDR＆アポイント', leadQual: 'リード選別', retention: 'リテンション＆ウィンバック',
      orderMgmt: '注文管理', billing: '請求＆回収', dataEntry: 'データ入力＆処理', virtualAssistant: 'バーチャルアシスタント',
      viewAll: 'すべて見る →', caseStudies: '導入事例', locations: '拠点', trustCenter: 'トラストセンター',
      careers: '採用情報', resources: 'リソース',
      staffAug: 'ITスタッフ拡充', customSoftware: 'エンタープライズソフトウェア', cloudDevops: 'クラウド＆DevOps', cybersecurity: 'サイバーセキュリティ',
      saas: 'カスタムSaaS開発', portals: 'エンタープライズポータル', mobile: 'モバイルアプリ', ecommerce: 'ECプラットフォーム',
      uiux: 'UI/UXデザイン', aiml: 'AI/ML開発', dataEng: 'データエンジニアリング＆BI', legacy: 'レガシー刷新',
      cloud: 'クラウド移行', devops: 'DevOps自動化', managed: 'マネージドサービス', iot: 'IoT開発',
      appSec: 'アプリケーションセキュリティ', penTest: 'ペネトレーションテスト', soc: 'SOCアズアサービス', blockchain: 'ブロックチェーン＆Web3',
      healthcare: 'ヘルスケア', fintech: 'フィンテック＆銀行', ecommerceInd: 'Eコマース＆小売',
      telecom: 'テレコム＆メディア', travel: '旅行＆ホスピタリティ', insurance: '保険',
      education: '教育＆EdTech', realestate: '不動産', logistics: '物流＆サプライチェーン',
      energy: 'エネルギー＆ユーティリティ', government: '政府', gaming: 'ゲーミング＆エンターテインメント',
      morocco: 'モロッコ（本社）', spain: 'スペイン',
      configureQuote: 'ソリューションを構成',
    },
    tr: {
      home: 'Ana Sayfa', services: 'Hizmetler', blog: 'Blog', bpoSilo: 'BPO & Çağrı Merkezi', itSilo: 'IT & Geliştirme', industries: 'Sektörler',
      pricing: 'Fiyatlar', about: 'Hakkımızda', contact: 'İletişim', getQuote: 'Teklif isteyin',
      omnichannel: 'Omnichannel Destek', contentMod: 'İçerik Moderasyonu', leadGen: 'Lead Üretimi', backOffice: 'Back-Office & Veri Girişi',
      inbound: 'Gelen Müşteri Hizmetleri', technical: 'Teknik Destek', livechat: 'Canlı Sohbet', social: 'Sosyal Medya Desteği',
      moderation: 'Güven ve Güvenlik', contentReview: 'İçerik İnceleme', fraudDetection: 'Dolandırıcılık Tespiti', compliance: 'Uyumluluk İzleme',
      outbound: 'Giden Satış', sdr: 'SDR & Randevu', leadQual: 'Lead Değerlendirme', retention: 'Müşteri Tutma & Win-back',
      orderMgmt: 'Sipariş Yönetimi', billing: 'Faturalama & Tahsilat', dataEntry: 'Veri Girişi & İşleme', virtualAssistant: 'Sanal Asistan',
      viewAll: 'Tümünü gör →', caseStudies: 'Başarı Hikayeleri', locations: 'Lokasyonlar', trustCenter: 'Güven Merkezi',
      careers: 'Kariyer', resources: 'Kaynaklar',
      staffAug: 'IT Personel Takviyesi', customSoftware: 'Kurumsal Yazılım', cloudDevops: 'Bulut & DevOps', cybersecurity: 'Siber Güvenlik',
      saas: 'Özel SaaS Geliştirme', portals: 'Kurumsal Portallar', mobile: 'Mobil Uygulamalar', ecommerce: 'E-ticaret Platformları',
      uiux: 'UI/UX Tasarım', aiml: 'AI/ML Geliştirme', dataEng: 'Veri Mühendisliği & BI', legacy: 'Legacy Modernizasyon',
      cloud: 'Bulut Göçü', devops: 'DevOps Otomasyonu', managed: 'Yönetilen Hizmetler', iot: 'IoT Geliştirme',
      appSec: 'Uygulama Güvenliği', penTest: 'Sızma Testi', soc: 'SOC Hizmeti', blockchain: 'Blockchain & Web3',
      healthcare: 'Sağlık', fintech: 'FinTech & Bankacılık', ecommerceInd: 'E-ticaret & Perakende',
      telecom: 'Telekom & Medya', travel: 'Seyahat & Konaklama', insurance: 'Sigortacılık',
      education: 'Eğitim & EdTech', realestate: 'Gayrimenkul', logistics: 'Lojistik & Tedarik Zinciri',
      energy: 'Enerji & Altyapı', government: 'Kamu', gaming: 'Oyun & Eğlence',
      morocco: 'Fas (Merkez)', spain: 'İspanya',
      configureQuote: 'Çözümünüzü yapılandırın',
    },
    ko: {
      home: '홈', services: '서비스', blog: '블로그', bpoSilo: 'BPO & 콜센터', itSilo: 'IT & 개발', industries: '산업',
      pricing: '가격', about: '소개', contact: '문의', getQuote: '견적 요청',
      omnichannel: '옴니채널 지원', contentMod: '콘텐츠 모더레이션', leadGen: '리드 생성', backOffice: '백오피스 & 데이터 입력',
      inbound: '인바운드 고객 지원', technical: '기술 지원', livechat: '실시간 채팅', social: 'SNS 지원',
      moderation: '신뢰 및 안전', contentReview: '콘텐츠 리뷰', fraudDetection: '사기 탐지', compliance: '컴플라이언스 모니터링',
      outbound: '아웃바운드 영업', sdr: 'SDR & 예약', leadQual: '리드 검증', retention: '고객 유지 & 윈백',
      orderMgmt: '주문 관리', billing: '결제 & 수금', dataEntry: '데이터 입력 & 처리', virtualAssistant: '가상 비서',
      viewAll: '모두 보기 →', caseStudies: '사례 연구', locations: '위치', trustCenter: '신뢰 센터',
      careers: '채용', resources: '리소스',
      staffAug: 'IT 인력 확충', customSoftware: '맞춤 기업 소프트웨어', cloudDevops: '클라우드 & DevOps', cybersecurity: '사이버 보안',
      saas: '맞춤 SaaS 개발', portals: '엔터프라이즈 포털', mobile: '모바일 앱', ecommerce: '이커머스 플랫폼',
      uiux: 'UI/UX 디자인', aiml: 'AI/ML 개발', dataEng: '데이터 엔지니어링 & BI', legacy: '레거시 현대화',
      cloud: '클라우드 마이그레이션', devops: 'DevOps 자동화', managed: '매니지드 서비스', iot: 'IoT 개발',
      appSec: '애플리케이션 보안', penTest: '침투 테스트', soc: 'SOC 서비스', blockchain: '블록체인 & Web3',
      healthcare: '헬스케어', fintech: '핀테크 & 은행', ecommerceInd: '이커머스 & 리테일',
      telecom: '통신 & 미디어', travel: '여행 & 호스피탈리티', insurance: '보험',
      education: '교육 & EdTech', realestate: '부동산', logistics: '물류 & 공급망',
      energy: '에너지 & 유틸리티', government: '정부', gaming: '게이밍 & 엔터테인먼트',
      morocco: '모로코 (본사)', spain: '스페인',
      configureQuote: '솔루션 구성',
    },
    sv: {
      home: 'Hem', services: 'Tjänster', blog: 'Blogg', bpoSilo: 'BPO & Callcenter', itSilo: 'IT & Utveckling', industries: 'Branscher',
      pricing: 'Priser', about: 'Om oss', contact: 'Kontakt', getQuote: 'Begär offert',
      omnichannel: 'Omnikanalstöd', contentMod: 'Innehållsmoderering', leadGen: 'Leadgenerering', backOffice: 'Back-Office & Datainmatning',
      inbound: 'Inkommande Kundtjänst', technical: 'Teknisk Support', livechat: 'Livechatt & Meddelanden', social: 'Sociala Medier',
      moderation: 'Trust & Safety', contentReview: 'Innehållsgranskning', fraudDetection: 'Bedrägeriupptäckt', compliance: 'Regelefterlevnad',
      outbound: 'Utgående Försäljning', sdr: 'SDR & Bokningar', leadQual: 'Leadkvalificering', retention: 'Retention & Win-back',
      orderMgmt: 'Orderhantering', billing: 'Fakturering & Inkasso', dataEntry: 'Datainmatning & Bearbetning', virtualAssistant: 'Virtuell Assistent',
      viewAll: 'Visa alla →', caseStudies: 'Fallstudier', locations: 'Platser', trustCenter: 'Förtroendecenter',
      careers: 'Karriär', resources: 'Resurser',
      staffAug: 'IT Staff Augmentation', customSoftware: 'Anpassad Företagsmjukvara', cloudDevops: 'Cloud & DevOps', cybersecurity: 'Cybersäkerhet',
      saas: 'Anpassad SaaS-utveckling', portals: 'Enterprise-portaler', mobile: 'Mobilappar', ecommerce: 'E-handelsplattformar',
      uiux: 'UI/UX Design', aiml: 'AI/ML-utveckling', dataEng: 'Data Engineering & BI', legacy: 'Legacy-modernisering',
      cloud: 'Molnmigrering', devops: 'DevOps-automatisering', managed: 'Hanterade Tjänster', iot: 'IoT-utveckling',
      appSec: 'Applikationssäkerhet', penTest: 'Penetrationstester', soc: 'SOC som Tjänst', blockchain: 'Blockchain & Web3',
      healthcare: 'Hälsovård', fintech: 'FinTech & Bank', ecommerceInd: 'E-handel & Detaljhandel',
      telecom: 'Telekom & Media', travel: 'Resor & Gästfrihet', insurance: 'Försäkring',
      education: 'Utbildning & EdTech', realestate: 'Fastigheter', logistics: 'Logistik & Leveranskedja',
      energy: 'Energi & Infrastruktur', government: 'Myndigheter', gaming: 'Spel & Underhållning',
      morocco: 'Marocko (Huvudkontor)', spain: 'Spanien',
      configureQuote: 'Konfigurera din lösning',
    },
    da: {
      home: 'Hjem', services: 'Tjenester', blog: 'Blog', bpoSilo: 'BPO & Callcenter', itSilo: 'IT & Udvikling', industries: 'Brancher',
      pricing: 'Priser', about: 'Om os', contact: 'Kontakt', getQuote: 'Anmod om tilbud',
      omnichannel: 'Omnikanalstøtte', contentMod: 'Indholdsmoderering', leadGen: 'Leadgenerering', backOffice: 'Back-Office & Dataindtastning',
      inbound: 'Indgående Kundeservice', technical: 'Teknisk Support', livechat: 'Live Chat & Beskeder', social: 'Sociale Medier',
      moderation: 'Trust & Safety', contentReview: 'Indholdsgennemgang', fraudDetection: 'Svindelafsløring', compliance: 'Overvågning af Overholdelse',
      outbound: 'Udgående Salg', sdr: 'SDR & Aftaler', leadQual: 'Leadkvalificering', retention: 'Fastholdelse & Win-back',
      orderMgmt: 'Ordrehåndtering', billing: 'Fakturering & Inkasso', dataEntry: 'Dataindtastning & Behandling', virtualAssistant: 'Virtuel Assistent',
      viewAll: 'Se alle →', caseStudies: 'Cases', locations: 'Lokationer', trustCenter: 'Tillidscenter',
      careers: 'Karriere', resources: 'Ressourcer',
      staffAug: 'IT Staff Augmentation', customSoftware: 'Tilpasset Virksomhedssoftware', cloudDevops: 'Cloud & DevOps', cybersecurity: 'Cybersikkerhed',
      saas: 'Tilpasset SaaS-udvikling', portals: 'Enterprise-portaler', mobile: 'Mobilapps', ecommerce: 'E-handelsplatforme',
      uiux: 'UI/UX Design', aiml: 'AI/ML-udvikling', dataEng: 'Data Engineering & BI', legacy: 'Legacy-modernisering',
      cloud: 'Cloudmigrering', devops: 'DevOps-automatisering', managed: 'Administrerede Tjenester', iot: 'IoT-udvikling',
      appSec: 'Applikationssikkerhed', penTest: 'Penetrationstest', soc: 'SOC som Tjeneste', blockchain: 'Blockchain & Web3',
      healthcare: 'Sundhed', fintech: 'FinTech & Bank', ecommerceInd: 'E-handel & Detailhandel',
      telecom: 'Telekom & Medier', travel: 'Rejser & Gæstfrihed', insurance: 'Forsikring',
      education: 'Uddannelse & EdTech', realestate: 'Ejendomme', logistics: 'Logistik & Forsyningskæde',
      energy: 'Energi & Forsyning', government: 'Offentlig Sektor', gaming: 'Gaming & Underholdning',
      morocco: 'Marokko (Hovedkontor)', spain: 'Spanien',
      configureQuote: 'Konfigurer din løsning',
    },
    hi: {
      home: 'होम', services: 'सेवाएं', blog: 'ब्लॉग', bpoSilo: 'BPO और कॉल सेंटर', itSilo: 'IT और विकास', industries: 'उद्योग',
      pricing: 'मूल्य', about: 'हमारे बारे में', contact: 'संपर्क', getQuote: 'कोटेशन अनुरोध',
      omnichannel: 'ऑम्नीचैनल सहायता', contentMod: 'सामग्री मॉडरेशन', leadGen: 'लीड जनरेशन', backOffice: 'बैक-ऑफिस और डेटा प्रविष्टि',
      inbound: 'इनबाउंड ग्राहक सेवा', technical: 'तकनीकी सहायता', livechat: 'लाइव चैट और मैसेजिंग', social: 'सोशल मीडिया केयर',
      moderation: 'ट्रस्ट और सेफ्टी', contentReview: 'सामग्री समीक्षा', fraudDetection: 'धोखाधड़ी पहचान', compliance: 'अनुपालन निगरानी',
      outbound: 'आउटबाउंड बिक्री', sdr: 'SDR और अपॉइंटमेंट', leadQual: 'लीड योग्यता', retention: 'ग्राहक प्रतिधारण',
      orderMgmt: 'ऑर्डर प्रबंधन', billing: 'बिलिंग और वसूली', dataEntry: 'डेटा प्रविष्टि और प्रसंस्करण', virtualAssistant: 'वर्चुअल सहायक',
      viewAll: 'सभी देखें →', caseStudies: 'केस स्टडी', locations: 'स्थान', trustCenter: 'ट्रस्ट सेंटर',
      careers: 'करियर', resources: 'संसाधन',
      staffAug: 'IT स्टाफ ऑग्मेंटेशन', customSoftware: 'एंटरप्राइज़ सॉफ्टवेयर', cloudDevops: 'क्लाउड और DevOps', cybersecurity: 'साइबर सुरक्षा',
      saas: 'कस्टम SaaS विकास', portals: 'एंटरप्राइज़ पोर्टल', mobile: 'मोबाइल ऐप्स', ecommerce: 'ई-कॉमर्स प्लेटफॉर्म',
      uiux: 'UI/UX डिज़ाइन', aiml: 'AI/ML विकास', dataEng: 'डेटा इंजीनियरिंग और BI', legacy: 'लिगेसी आधुनिकीकरण',
      cloud: 'क्लाउड माइग्रेशन', devops: 'DevOps ऑटोमेशन', managed: 'मैनेज्ड सर्विसेज', iot: 'IoT विकास',
      appSec: 'एप्लिकेशन सुरक्षा', penTest: 'पेनेट्रेशन टेस्टिंग', soc: 'SOC सेवा', blockchain: 'ब्लॉकचेन और Web3',
      healthcare: 'स्वास्थ्य सेवा', fintech: 'फिनटेक और बैंकिंग', ecommerceInd: 'ई-कॉमर्स और रिटेल',
      telecom: 'टेलीकॉम और मीडिया', travel: 'यात्रा और आतिथ्य', insurance: 'बीमा',
      education: 'शिक्षा और EdTech', realestate: 'रियल एस्टेट', logistics: 'लॉजिस्टिक्स और सप्लाई चेन',
      energy: 'ऊर्जा और यूटिलिटीज', government: 'सरकार', gaming: 'गेमिंग और मनोरंजन',
      morocco: 'मोरक्को (मुख्यालय)', spain: 'स्पेन',
      configureQuote: 'अपना समाधान कॉन्फ़िगर करें',
    },
    ro: {
      home: 'Acasă', services: 'Servicii', blog: 'Blog', bpoSilo: 'BPO & Call Center', itSilo: 'IT & Dezvoltare', industries: 'Industrii',
      pricing: 'Prețuri', about: 'Despre noi', contact: 'Contact', getQuote: 'Solicitați o ofertă',
      omnichannel: 'Suport Omnichannel', contentMod: 'Moderare Conținut', leadGen: 'Generare Leaduri', backOffice: 'Back-Office & Introducere Date',
      inbound: 'Servicii Clienți Inbound', technical: 'Suport Tehnic', livechat: 'Chat Live & Mesagerie', social: 'Social Media',
      moderation: 'Trust & Safety', contentReview: 'Revizuire Conținut', fraudDetection: 'Detectare Fraudă', compliance: 'Monitorizare Conformitate',
      outbound: 'Vânzări Outbound', sdr: 'SDR & Programări', leadQual: 'Calificare Leaduri', retention: 'Retenție & Win-back',
      orderMgmt: 'Gestionare Comenzi', billing: 'Facturare & Colectare', dataEntry: 'Introducere & Procesare Date', virtualAssistant: 'Asistent Virtual',
      viewAll: 'Vezi tot →', caseStudies: 'Studii de Caz', locations: 'Locații', trustCenter: 'Centru de Încredere',
      careers: 'Cariere', resources: 'Resurse',
      staffAug: 'IT Staff Augmentation', customSoftware: 'Software Enterprise', cloudDevops: 'Cloud & DevOps', cybersecurity: 'Securitate Cibernetică',
      saas: 'Dezvoltare SaaS', portals: 'Portaluri Enterprise', mobile: 'Aplicații Mobile', ecommerce: 'Platforme E-commerce',
      uiux: 'Design UI/UX', aiml: 'Dezvoltare AI/ML', dataEng: 'Inginerie Date & BI', legacy: 'Modernizare Legacy',
      cloud: 'Migrare Cloud', devops: 'Automatizare DevOps', managed: 'Servicii Gestionate', iot: 'Dezvoltare IoT',
      appSec: 'Securitate Aplicații', penTest: 'Teste de Penetrare', soc: 'SOC ca Serviciu', blockchain: 'Blockchain & Web3',
      healthcare: 'Sănătate', fintech: 'FinTech & Bănci', ecommerceInd: 'E-commerce & Retail',
      telecom: 'Telecom & Media', travel: 'Turism & Ospitalitate', insurance: 'Asigurări',
      education: 'Educație & EdTech', realestate: 'Imobiliare', logistics: 'Logistică & Lanț de Aprovizionare',
      energy: 'Energie & Utilități', government: 'Guvern', gaming: 'Gaming & Divertisment',
      morocco: 'Maroc (Sediu)', spain: 'Spania',
      configureQuote: 'Configurați soluția dvs.',
    },
    cs: {
      home: 'Domů', services: 'Služby', blog: 'Blog', bpoSilo: 'BPO & Call Centrum', itSilo: 'IT & Vývoj', industries: 'Odvětví',
      pricing: 'Ceník', about: 'O nás', contact: 'Kontakt', getQuote: 'Vyžádat nabídku',
      omnichannel: 'Omnikanálová podpora', contentMod: 'Moderace obsahu', leadGen: 'Generování leadů', backOffice: 'Back-Office & Zadávání dat',
      inbound: 'Příchozí zákaznická péče', technical: 'Technická podpora', livechat: 'Live chat & Zprávy', social: 'Sociální média',
      moderation: 'Trust & Safety', contentReview: 'Kontrola obsahu', fraudDetection: 'Detekce podvodů', compliance: 'Monitoring shody',
      outbound: 'Odchozí prodej', sdr: 'SDR & Sjednání schůzek', leadQual: 'Kvalifikace leadů', retention: 'Retence & Win-back',
      orderMgmt: 'Správa objednávek', billing: 'Fakturace & Inkaso', dataEntry: 'Zadávání & Zpracování dat', virtualAssistant: 'Virtuální asistent',
      viewAll: 'Zobrazit vše →', caseStudies: 'Případové studie', locations: 'Lokace', trustCenter: 'Centrum důvěry',
      careers: 'Kariéra', resources: 'Zdroje',
      staffAug: 'IT Staff Augmentation', customSoftware: 'Podnikový software', cloudDevops: 'Cloud & DevOps', cybersecurity: 'Kybernetická bezpečnost',
      saas: 'Vývoj SaaS na míru', portals: 'Enterprise portály', mobile: 'Mobilní aplikace', ecommerce: 'E-commerce platformy',
      uiux: 'UI/UX Design', aiml: 'Vývoj AI/ML', dataEng: 'Datové inženýrství & BI', legacy: 'Modernizace legacy',
      cloud: 'Migrace do cloudu', devops: 'Automatizace DevOps', managed: 'Spravované služby', iot: 'Vývoj IoT',
      appSec: 'Bezpečnost aplikací', penTest: 'Penetrační testy', soc: 'SOC jako služba', blockchain: 'Blockchain & Web3',
      healthcare: 'Zdravotnictví', fintech: 'FinTech & Bankovnictví', ecommerceInd: 'E-commerce & Retail',
      telecom: 'Telekomunikace & Média', travel: 'Cestování & Pohostinství', insurance: 'Pojišťovnictví',
      education: 'Vzdělávání & EdTech', realestate: 'Nemovitosti', logistics: 'Logistika & Dodavatelský řetězec',
      energy: 'Energetika & Utility', government: 'Veřejný sektor', gaming: 'Gaming & Zábava',
      morocco: 'Maroko (Sídlo)', spain: 'Španělsko',
      configureQuote: 'Nakonfigurujte své řešení',
    },
    hu: {
      home: 'Kezdőlap', services: 'Szolgáltatások', blog: 'Blog', bpoSilo: 'BPO & Call Center', itSilo: 'IT & Fejlesztés', industries: 'Iparágak',
      pricing: 'Árak', about: 'Rólunk', contact: 'Kapcsolat', getQuote: 'Ajánlatkérés',
      omnichannel: 'Omnichannel támogatás', contentMod: 'Tartalommoderáció', leadGen: 'Lead generálás', backOffice: 'Back-Office & Adatbevitel',
      inbound: 'Bejövő ügyfélszolgálat', technical: 'Technikai támogatás', livechat: 'Élő chat & Üzenetek', social: 'Közösségi média',
      moderation: 'Trust & Safety', contentReview: 'Tartalom áttekintés', fraudDetection: 'Csalás felderítés', compliance: 'Megfelelőség felügyelet',
      outbound: 'Kimenő értékesítés', sdr: 'SDR & Időpontegyeztetés', leadQual: 'Lead minősítés', retention: 'Ügyfélmegtartás & Win-back',
      orderMgmt: 'Rendeléskezelés', billing: 'Számlázás & Behajtás', dataEntry: 'Adatbevitel & Feldolgozás', virtualAssistant: 'Virtuális asszisztens',
      viewAll: 'Összes megtekintése →', caseStudies: 'Esettanulmányok', locations: 'Helyszínek', trustCenter: 'Bizalmi Központ',
      careers: 'Karrier', resources: 'Források',
      staffAug: 'IT Staff Augmentation', customSoftware: 'Vállalati szoftver', cloudDevops: 'Cloud & DevOps', cybersecurity: 'Kiberbiztonság',
      saas: 'Egyedi SaaS fejlesztés', portals: 'Vállalati portálok', mobile: 'Mobilalkalmazások', ecommerce: 'E-kereskedelmi platformok',
      uiux: 'UI/UX Tervezés', aiml: 'AI/ML fejlesztés', dataEng: 'Adatmérnökség & BI', legacy: 'Legacy modernizáció',
      cloud: 'Felhőmigráció', devops: 'DevOps automatizálás', managed: 'Felügyelt szolgáltatások', iot: 'IoT fejlesztés',
      appSec: 'Alkalmazásbiztonság', penTest: 'Penetrációs tesztek', soc: 'SOC szolgáltatás', blockchain: 'Blockchain & Web3',
      healthcare: 'Egészségügy', fintech: 'FinTech & Bank', ecommerceInd: 'E-kereskedelem & Kiskereskedelem',
      telecom: 'Telekommunikáció & Média', travel: 'Utazás & Vendéglátás', insurance: 'Biztosítás',
      education: 'Oktatás & EdTech', realestate: 'Ingatlan', logistics: 'Logisztika & Ellátási lánc',
      energy: 'Energia & Közművek', government: 'Kormányzat', gaming: 'Gaming & Szórakoztatás',
      morocco: 'Marokkó (Központ)', spain: 'Spanyolország',
      configureQuote: 'Konfigurálja megoldását',
    },
    fi: {
      home: 'Etusivu', services: 'Palvelut', blog: 'Blogi', bpoSilo: 'BPO & Puhelinkeskus', itSilo: 'IT & Kehitys', industries: 'Toimialat',
      pricing: 'Hinnat', about: 'Meistä', contact: 'Yhteystiedot', getQuote: 'Pyydä tarjous',
      omnichannel: 'Omnikanava-tuki', contentMod: 'Sisällönvalvonta', leadGen: 'Liidien generointi', backOffice: 'Back-Office & Tietojen syöttö',
      inbound: 'Saapuva asiakaspalvelu', technical: 'Tekninen tuki', livechat: 'Live chat & Viestintä', social: 'Sosiaalinen media',
      moderation: 'Trust & Safety', contentReview: 'Sisällön tarkistus', fraudDetection: 'Petostunnistus', compliance: 'Vaatimustenmukaisuus',
      outbound: 'Lähtevä myynti', sdr: 'SDR & Ajanvaraus', leadQual: 'Liidien laadinta', retention: 'Asiakaspito & Win-back',
      orderMgmt: 'Tilaushallinta', billing: 'Laskutus & Perintä', dataEntry: 'Tietojen syöttö & Käsittely', virtualAssistant: 'Virtuaaliavustaja',
      viewAll: 'Näytä kaikki →', caseStudies: 'Tapaustutkimukset', locations: 'Sijainnit', trustCenter: 'Luottamuskeskus',
      careers: 'Ura', resources: 'Resurssit',
      staffAug: 'IT Staff Augmentation', customSoftware: 'Räätälöity yritysohjelmisto', cloudDevops: 'Cloud & DevOps', cybersecurity: 'Kyberturvallisuus',
      saas: 'Räätälöity SaaS-kehitys', portals: 'Yritysportaalit', mobile: 'Mobiilisovellukset', ecommerce: 'Verkkokauppa-alustat',
      uiux: 'UI/UX Suunnittelu', aiml: 'AI/ML-kehitys', dataEng: 'Datatekniikka & BI', legacy: 'Legacy-modernisointi',
      cloud: 'Pilvimigraatio', devops: 'DevOps-automaatio', managed: 'Hallinnoidut palvelut', iot: 'IoT-kehitys',
      appSec: 'Sovellusturvallisuus', penTest: 'Tunkeutumistestaus', soc: 'SOC palveluna', blockchain: 'Blockchain & Web3',
      healthcare: 'Terveydenhuolto', fintech: 'FinTech & Pankkitoiminta', ecommerceInd: 'Verkkokauppa & Vähittäismyynti',
      telecom: 'Televiestintä & Media', travel: 'Matkailu & Vieraanvaraisuus', insurance: 'Vakuutus',
      education: 'Koulutus & EdTech', realestate: 'Kiinteistöt', logistics: 'Logistiikka & Toimitusketju',
      energy: 'Energia & Infrastruktuuri', government: 'Julkinen sektori', gaming: 'Pelaaminen & Viihde',
      morocco: 'Marokko (Pääkonttori)', spain: 'Espanja',
      configureQuote: 'Määritä ratkaisusi',
    },
  };
  // Fallback for unsupported languages
  return m[language] || m.en;
};

/* ───────────────────── Component ───────────────────── */
export function GlassHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const [expandedMobileSection, setExpandedMobileSection] = useState<string | null>(null);
  const [user, setUser] = useState<SupabaseUser | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { language, switchLanguage, t, dir } = useLanguage();
  const headerQuoteHref = startHref(language, location.pathname, 'header-quote');
  // On the order wizard itself, the header quote CTA would reset an in-progress form: hide it.
  const onOrderWizard = /^(\/[a-z]{2}(-[a-z]{2})?)?\/(start|quote)(\/|$)/i.test(location.pathname);
  
  const currentLang = languages.find(l => l.code === language) || languages[0];
  const L = getSiloLabels(language as SupportedLanguage);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auth state
  useEffect(() => {
    const fetchUserRole = async (userId: string) => {
      try {
        const { data, error } = await supabase.rpc('get_user_role', { _user_id: userId });
        if (!error && data) setUserRole(data);
        else setUserRole('client');
      } catch { setUserRole('client'); }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) setTimeout(() => fetchUserRole(session.user.id), 0);
      else setUserRole(null);
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) fetchUserRole(session.user.id);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => { setIsMobileMenuOpen(false); }, [location.pathname]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate(`/${language}`);
  };

  const getDashboardLink = () => {
    switch (userRole) {
      case 'admin': case 'manager': return '/admin/dashboard';
      case 'agent': return `/${language}/agent`;
      case 'client': return `/${language}/employer/dashboard`;
      default: return `/${language}/candidate/dashboard`;
    }
  };

  // Deferred nav mount for performance
  const [navMounted, setNavMounted] = useState(false);
  useEffect(() => {
    const idle = 'requestIdleCallback' in window
      ? (window as any).requestIdleCallback(() => setNavMounted(true), { timeout: 500 })
      : setTimeout(() => setNavMounted(true), 150);
    return () => {
      if ('requestIdleCallback' in window) (window as any).cancelIdleCallback(idle);
      else clearTimeout(idle);
    };
  }, []);

  const NavPlaceholder = useCallback(({ icon: Icon, label }: { icon: any; label: string }) => (
    <div className="relative z-10 flex max-w-max flex-1 items-center justify-center">
      <button
        className="group inline-flex h-10 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium text-foreground cursor-default"
        disabled aria-hidden="true"
      >
        <Icon className="h-4 w-4 mr-1" />
        {label}
        <ChevronDown className="relative top-[1px] ml-1 h-3 w-3" aria-hidden="true" />
      </button>
    </div>
  ), []);

  /* ────────── BPO Silo Column Data ────────── */
  const bpoColumns = [
    {
      title: L.omnichannel,
      icon: Headphones,
      color: 'text-cyan',
      items: [
        { label: L.inbound, href: 'inbound-customer-care', icon: Phone },
        { label: L.technical, href: 'technical-support', icon: Wrench },
        { label: L.livechat, href: 'live-chat-messaging', icon: MessageSquare },
        { label: L.social, href: 'social-media-care', icon: Users }
      ],
    },
    {
      title: L.contentMod,
      icon: Shield,
      color: 'text-orange',
      items: [
        { label: L.moderation, href: 'trust-safety-compliance', icon: Shield },
        { label: L.contentReview, href: 'content-review', icon: BookOpen },
        { label: L.fraudDetection, href: 'fraud-detection', icon: Lock },
        { label: L.compliance, href: 'compliance-monitoring', icon: Target }
      ],
    },
    {
      title: L.leadGen,
      icon: TrendingUp,
      color: 'text-cyan',
      items: [
        { label: L.outbound, href: 'outbound-sales', icon: TrendingUp },
        { label: L.sdr, href: 'sdr-appointment-setting', icon: Target },
        { label: L.leadQual, href: 'lead-qualification', icon: BarChart3 },
        { label: L.retention, href: 'retention-winback', icon: Heart }
      ],
    },
    {
      title: L.backOffice,
      icon: FileText,
      color: 'text-orange',
      items: [
        { label: L.orderMgmt, href: 'order-management', icon: ShoppingCart },
        { label: L.billing, href: 'billing-collections', icon: FileText },
        { label: L.dataEntry, href: 'data-entry-processing', icon: Database },
        { label: L.virtualAssistant, href: 'virtual-assistant', icon: MessageSquare }
      ],
    }
  ];

  /* ────────── IT Silo Column Data ────────── */
  const itColumns = [
    {
      title: L.staffAug,
      icon: Users,
      color: 'text-orange',
      items: [
        { label: L.staffAug, href: 'staff-augmentation', icon: Users },
        { label: L.uiux, href: 'ui-ux-design', icon: Sparkles },
        { label: L.aiml, href: 'ai-ml-development', icon: Zap },
        { label: L.dataEng, href: 'data-engineering-bi', icon: Database }
      ],
    },
    {
      title: L.customSoftware,
      icon: Code,
      color: 'text-cyan',
      items: [
        { label: L.saas, href: 'custom-saas', icon: Cloud },
        { label: L.portals, href: 'enterprise-portals', icon: LayoutDashboard },
        { label: L.mobile, href: 'mobile-apps', icon: Smartphone },
        { label: L.ecommerce, href: 'ecommerce', icon: ShoppingCart }
      ],
    },
    {
      title: L.cloudDevops,
      icon: Cloud,
      color: 'text-orange',
      items: [
        { label: L.cloud, href: 'cloud-devops', icon: Cloud },
        { label: L.devops, href: 'devops-automation', icon: Settings },
        { label: L.managed, href: 'managed-services', icon: Wrench },
        { label: L.iot, href: 'iot-development', icon: Smartphone }
      ],
    },
    {
      title: L.cybersecurity,
      icon: Lock,
      color: 'text-cyan',
      items: [
        { label: L.appSec, href: 'cybersecurity-appsec', icon: Lock },
        { label: L.penTest, href: 'penetration-testing', icon: Shield },
        { label: L.soc, href: 'soc-as-a-service', icon: Target },
        { label: L.blockchain, href: 'blockchain-web3', icon: Zap }
      ],
    }
  ];

  /* ────────── Industries Data ────────── */
  const industriesList = [
    { label: L.healthcare, href: 'healthcare', icon: Stethoscope, emoji: '🏥' },
    { label: L.fintech, href: 'fintech-banking', icon: CreditCard, emoji: '💳' },
    { label: L.ecommerceInd, href: 'ecommerce-retail', icon: ShoppingCart, emoji: '🛒' },
    { label: L.telecom, href: 'telecom-media', icon: Smartphone, emoji: '📱' },
    { label: L.travel, href: 'travel-hospitality', icon: MapPin, emoji: '✈️' },
    { label: L.insurance, href: 'insurance', icon: Shield, emoji: '🛡️' },
    { label: L.education, href: 'education-edtech', icon: GraduationCap, emoji: '🎓' },
    { label: L.realestate, href: 'real-estate', icon: Building2, emoji: '🏢' },
    { label: L.logistics, href: 'logistics-supply-chain', icon: Briefcase, emoji: '📦' },
    { label: L.energy, href: 'energy-utilities', icon: Zap, emoji: '⚡' },
    { label: L.government, href: 'government', icon: Building2, emoji: '🏛️' },
    { label: L.gaming, href: 'gaming-entertainment', icon: Sparkles, emoji: '🎮' }
  ];

  /* ────────── Mega Column Renderer ────────── */
  const MegaColumn = ({ col, basePath }: { col: typeof bpoColumns[0]; basePath: string }) => (
    <div className="space-y-1">
      <div className={`flex items-center gap-2 ${col.color} font-semibold text-sm mb-3`}>
        <col.icon className="h-4 w-4" />
        {col.title}
      </div>
      {col.items.map((item) => (
        <Link
          key={item.href}
          to={`/${language}/services/${basePath}/${item.href}`}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors py-1.5 rounded-md hover:bg-primary/5 px-2 -mx-2"
        >
          <item.icon className="h-3.5 w-3.5 text-muted-foreground" />
          {item.label}
        </Link>
      ))}
    </div>
  );

  return (
    <header
      className={`
        fixed top-0 inset-x-0 z-50 transition-all duration-300
        ${isScrolled 
          ? 'bg-muted/20 backdrop-blur-xl border-b border-border shadow-lg' 
          : 'bg-white/90 backdrop-blur-xl'
        }
      `}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to={`/${language}`} className="flex items-center gap-2 flex-shrink-0">
            <Logo variant="full" size="md" darkMode={false} />
          </Link>

          {/* ═══════════ Desktop Navigation ═══════════ */}
          <nav aria-label="Main navigation" className="hidden min-[1600px]:flex items-center gap-1">
            <Link
              to={`/${language}`}
              className={`text-sm font-medium transition-colors px-3 py-2 rounded-md ${location.pathname === `/${language}`? 'text-primary': 'text-foreground hover:text-primary hover:bg-primary/5'}`}
            >
              {L.home}
            </Link>
            
            {/* ──── SILO 1: BPO & Call Center ──── */}
            {navMounted ? (
            <NavigationMenu aria-label={L.bpoSilo}>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="bg-transparent text-foreground hover:text-primary hover:bg-primary/5 data-[state=open]:bg-primary/10 text-sm">
                    <Headphones className="h-4 w-4 mr-1.5" />
                    {L.bpoSilo}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent className="!bg-white border-border shadow-xl">
                    <div className="grid gap-6 p-6 w-[850px] grid-cols-4">
                      {bpoColumns.map((col) => (
                        <MegaColumn key={col.title} col={col} basePath="bpo" />
                      ))}
                    </div>
                    <div className="border-t border-border px-6 py-3 flex items-center justify-between">
                      <Link to={`/${language}/services/bpo`} className="text-xs text-cyan hover:text-cyan/80 font-medium">
                        {L.viewAll}
                      </Link>
                      <Link to={`/${language}/start`}>
                        <Button size="sm" className="bg-gradient-to-r from-cyan to-orange text-white text-xs h-8">
                          {L.getQuote}
                        </Button>
                      </Link>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
            ) : <NavPlaceholder icon={Headphones} label={L.bpoSilo} />}

            {/* ──── SILO 2: IT & Development ──── */}
            {navMounted ? (
            <NavigationMenu aria-label={L.itSilo}>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="bg-transparent text-foreground hover:text-primary hover:bg-primary/5 data-[state=open]:bg-primary/10 text-sm">
                    <Code className="h-4 w-4 mr-1.5" />
                    {L.itSilo}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent className="!bg-white border-border shadow-xl">
                    <div className="grid gap-6 p-6 w-[850px] grid-cols-4">
                      {itColumns.map((col) => (
                        <MegaColumn key={col.title} col={col} basePath="digital-studio" />
                      ))}
                    </div>
                    <div className="border-t border-border px-6 py-3 flex items-center justify-between">
                      <Link to={`/${language}/services/digital-studio`} className="text-xs text-orange hover:text-orange/80 font-medium">
                        {L.viewAll}
                      </Link>
                      <Link to={`/${language}/start`}>
                        <Button size="sm" className="bg-gradient-to-r from-orange to-orange-light text-white text-xs h-8">
                          {L.getQuote}
                        </Button>
                      </Link>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
            ) : <NavPlaceholder icon={Code} label={L.itSilo} />}

            {/* ──── SILO 3: Industries ──── */}
            {navMounted ? (
            <NavigationMenu aria-label={L.industries}>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="bg-transparent text-foreground hover:text-primary hover:bg-primary/5 data-[state=open]:bg-primary/10 text-sm">
                    <Building2 className="h-4 w-4 mr-1.5" />
                    {L.industries}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent className="!bg-white border-border shadow-xl">
                    <div className="grid gap-2 p-6 w-[500px] grid-cols-2">
                      {industriesList.map((ind) => (
                        <Link
                          key={ind.href}
                          to={`/${language}/industries/${ind.href}`}
                          className="flex items-center gap-3 rounded-lg p-2.5 hover:bg-primary/5 transition-colors"
                        >
                          <span className="text-lg">{ind.emoji}</span>
                          <span className="text-sm text-foreground font-medium">{ind.label}</span>
                        </Link>
                      ))}
                    </div>
                    <div className="border-t border-border px-6 py-3">
                      <Link to={`/${language}/industries`} className="text-xs text-cyan hover:text-cyan/80 font-medium">
                        {L.viewAll}
                      </Link>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
            ) : <NavPlaceholder icon={Building2} label={L.industries} />}

            {/* ──── SILO 4: Resources ──── */}
            {navMounted ? (
            <NavigationMenu aria-label={L.resources}>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="bg-transparent text-foreground hover:text-primary hover:bg-primary/5 data-[state=open]:bg-primary/10 text-sm">
                    <BookOpen className="h-4 w-4 mr-1.5" />
                    {L.resources}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent className="!bg-white border-border shadow-xl">
                    <div className="grid gap-2 p-6 w-[400px] grid-cols-1">
                      {[
                        { label: L.blog, href: `/${language}/blog`, icon: FileText, emoji: '📝' },
                        { label: L.caseStudies, href: `/${language}/case-studies`, icon: BookOpen, emoji: '📊' },
                        { label: language === 'fr' ? 'Glossaire' : language === 'es' ? 'Glosario' : language === 'de' ? 'Glossar' : 'Glossary', href: `/${language}/glossary`, icon: BookOpen, emoji: '📖' },
                        { label: language === 'fr' ? 'Académie' : language === 'es' ? 'Academia' : language === 'de' ? 'Akademie' : 'Academy', href: `/${language}/academy`, icon: GraduationCap, emoji: '🎓' },
                        { label: language === 'fr' ? 'Webinaires' : language === 'es' ? 'Webinarios' : language === 'de' ? 'Webinare' : 'Webinars', href: `/${language}/webinars`, icon: Users, emoji: '🎥' },
                        { label: 'FAQ', href: `/${language}/faq`, icon: MessageSquare, emoji: '❓' },
                        { label: language === 'fr' ? 'Calculateur de Coûts' : language === 'es' ? 'Calculadora de Costes' : language === 'de' ? 'Kostenrechner' : 'Cost Calculator', href: `/${language}/cost-calculator`, icon: BarChart3, emoji: '🧮' }
                      ].map((item) => (
                        <Link
                          key={item.href}
                          to={item.href}
                          className="flex items-center gap-3 rounded-lg p-2.5 hover:bg-primary/5 transition-colors"
                        >
                          <span className="text-lg">{item.emoji}</span>
                          <span className="text-sm text-foreground font-medium">{item.label}</span>
                        </Link>
                      ))}
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
            ) : <NavPlaceholder icon={BookOpen} label={L.resources} />}

            {/* ──── SILO 5: Company ──── */}
            {navMounted ? (
            <NavigationMenu aria-label={language === 'fr' ? 'Entreprise' : language === 'es' ? 'Empresa' : language === 'de' ? 'Unternehmen' : language === 'ar' ? 'الشركة' : 'Company'}>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="bg-transparent text-foreground hover:text-primary hover:bg-primary/5 data-[state=open]:bg-primary/10 text-sm">
                    <Building2 className="h-4 w-4 mr-1.5" />
                    {language === 'fr' ? 'Entreprise' : language === 'es' ? 'Empresa' : language === 'de' ? 'Unternehmen' : language === 'ar' ? 'الشركة' : 'Company'}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent className="!bg-white border-border shadow-xl">
                     <div className="grid gap-2 p-6 w-[400px] grid-cols-1">
                      {[
                        { label: L.about, href: `/${language}/about`, emoji: '🏢' },
                        { label: language === 'fr' ? 'Équipe de Direction' : language === 'es' ? 'Equipo Directivo' : language === 'de' ? 'Führungsteam' : 'Leadership', href: `/${language}/about/leadership`, emoji: '👥' },
                        { label: L.trustCenter, href: `/${language}/trust-center`, emoji: '🛡️' },
                        { label: language === 'fr' ? 'RSE & Responsabilité' : language === 'es' ? 'ESG & Responsabilidad' : language === 'de' ? 'ESG & Verantwortung' : 'ESG & Responsibility', href: `/${language}/esg`, emoji: '🌱' },
                        { label: language === 'fr' ? 'Nos Clients' : language === 'es' ? 'Clientes' : language === 'de' ? 'Kunden' : 'Our Clients', href: `/${language}/clients`, emoji: '🤝' },
                        { label: language === 'fr' ? 'Pourquoi Nous' : language === 'es' ? '¿Por qué Nosotros?' : language === 'de' ? 'Warum Wir' : 'Why Choose Us', href: `/${language}/why-choose-us`, emoji: '⭐' },
                        { label: L.careers, href: `/${language}/careers`, emoji: '💼' },
                        { label: L.contact, href: `/${language}/contact`, emoji: '📞' },
                        { label: L.pricing, href: `/${language}/pricing-models`, emoji: '💰' },
                        { label: L.locations, href: `/${language}/contact/morocco`, emoji: '📍' }
                      ].map((item) => (
                        <Link
                          key={item.href}
                          to={item.href}
                          className="flex items-center gap-3 rounded-lg p-2.5 hover:bg-primary/5 transition-colors"
                        >
                          <span className="text-lg">{item.emoji}</span>
                          <span className="text-sm text-foreground font-medium">{item.label}</span>
                        </Link>
                      ))}
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
            ) : <NavPlaceholder icon={Building2} label={language === 'fr' ? 'Entreprise' : 'Company'} />}
          </nav>

          {/* ═══════════ Right Side Actions ═══════════ */}
          <div className="hidden min-[1600px]:flex items-center gap-3 flex-shrink-0">
            {/* Language Switcher */}
            <div className="relative">
              <button
                onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                aria-label="Select language"
                className="flex items-center gap-2 text-foreground hover:text-primary transition-colors px-2 py-2 rounded-lg hover:bg-primary/5"
              >
                <Globe className="h-4 w-4" />
                <span className="text-sm font-medium">{currentLang.flag}</span>
                <ChevronDown className="h-3 w-3" />
              </button>
              
              <AnimatePresence>
                {isLangMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute top-full end-0 mt-2 w-48 bg-white/95 backdrop-blur-xl border border-border rounded-xl shadow-xl overflow-hidden z-50 max-h-[70vh] overflow-y-auto"
                  >
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => { switchLanguage(lang.code); setIsLangMenuOpen(false); }}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors
                          ${language === lang.code ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-primary/5 hover:text-primary'}`}
                      >
                        <span>{lang.flag}</span>
                        <span>{lang.name}</span>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Auth */}
            <div className="flex items-center gap-2">
              {user ? (
                <>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="flex items-center gap-2 px-2 text-foreground hover:bg-primary/5">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="bg-primary/20 text-primary text-sm font-semibold">
                            {user.email?.charAt(0).toUpperCase() || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <span className="hidden lg:inline text-sm text-foreground font-medium max-w-[120px] truncate">
                          {user.user_metadata?.full_name || user.email?.split('@')[0] || ''}
                        </span>
                        <ChevronDown className="h-3 w-3 text-muted-foreground" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56 bg-white border-border shadow-xl">
                      <DropdownMenuLabel className="text-foreground font-normal">
                        <p className="text-sm font-medium text-foreground">{user.user_metadata?.full_name || user.email?.split('@')[0]}</p>
                        <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator className="bg-border" />
                      <DropdownMenuItem asChild className="text-foreground hover:bg-primary/5 cursor-pointer">
                        <Link to={`/${language}/dashboard`}>
                          <LayoutDashboard className="mr-2 h-4 w-4" />
                          {t.nav?.dashboard || 'My Dashboard'}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild className="text-foreground hover:bg-primary/5 cursor-pointer">
                        <Link to={`/${language}/profile`}>
                          <User className="mr-2 h-4 w-4" />
                          {t.nav?.profile || 'My Profile'}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild className="text-foreground hover:bg-primary/5 cursor-pointer">
                        <Link to={`/${language}/portal`}>
                          <MessageSquare className="mr-2 h-4 w-4" />
                          {(t.nav as Record<string, string>)?.messages || 'Messages'}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-border" />
                      <DropdownMenuItem onClick={handleSignOut} className="text-destructive hover:bg-destructive/10 cursor-pointer">
                        <LogOut className="mr-2 h-4 w-4" />
                        {t.nav?.logout || 'Log Out'}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <>
                  <Link to={`/${language}/auth`}>
                    <Button variant="ghost" size="sm" className="text-foreground hover:text-primary hover:bg-primary/5">
                      <LogIn className="h-4 w-4 mr-1" />
                      {t.nav?.login || 'Login'}
                    </Button>
                  </Link>
{!onOrderWizard && (
                  <Button asChild size="sm" className="bg-gradient-to-r from-orange to-orange-light text-white font-semibold shadow-cta hover:shadow-lg transition-all">
                    <Link to={headerQuoteHref}>{L.getQuote}</Link>
                  </Button>
)}
                </>
              )}
            </div>
          </div>

          {/* ═══════════ Compact navigation (below 1600px) ═══════════ */}
          <nav aria-label="Quick navigation" className="hidden lg:flex min-[1600px]:hidden items-center gap-1 ml-auto mr-2">
            <Link to={`/${language}/services/bpo`} className="text-sm font-medium text-foreground hover:text-primary px-3 py-2 rounded-md">{L.bpoSilo}</Link>
            <Link to={`/${language}/industries`} className="text-sm font-medium text-foreground hover:text-primary px-3 py-2 rounded-md">{L.industries || 'Industries'}</Link>
            <Link to={`/${language}/pricing`} className="text-sm font-medium text-foreground hover:text-primary px-3 py-2 rounded-md">{L.pricing}</Link>
            <Link to={`/${language}/contact`} className="text-sm font-medium text-foreground hover:text-primary px-3 py-2 rounded-md">{L.contact}</Link>
          </nav>
{!onOrderWizard && (
          <Button asChild size="sm" className="hidden sm:inline-flex min-[1600px]:hidden mr-2 bg-gradient-to-r from-cyan to-orange text-white">
            <Link to={headerQuoteHref}>{L.getQuote}</Link>
          </Button>
)}

          {/* ═══════════ Mobile Toggle ═══════════ */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="min-[1600px]:hidden text-foreground p-2"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* ═══════════ Mobile Menu ═══════════ */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="min-[1600px]:hidden bg-white/95 backdrop-blur-xl border-t border-border max-h-[80vh] overflow-y-auto"
          >
            <div className="container mx-auto px-4 py-6">
              <nav className="flex flex-col gap-1">
                {/* Mobile Silo 1: BPO */}
                <div>
                  <button
                    onClick={() => setExpandedMobileSection(expandedMobileSection === 'bpo' ? null : 'bpo')}
                    className="flex items-center justify-between w-full py-3 text-foreground font-medium"
                  >
                    <span className="flex items-center gap-2">
                      <Headphones className="h-4 w-4 text-cyan" />
                      {L.bpoSilo}
                    </span>
                    <ChevronRight className={`h-4 w-4 transition-transform ${expandedMobileSection === 'bpo' ? 'rotate-90' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {expandedMobileSection === 'bpo' && (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                        <div className="ps-4 pb-3 space-y-4">
                          {bpoColumns.map((col) => (
                            <div key={col.title}>
                              <div className={`text-xs font-semibold uppercase tracking-wider ${col.color} mb-2`}>{col.title}</div>
                              {col.items.map((item) => (
                                <Link key={item.href} to={`/${language}/services/bpo/${item.href}`} className="block text-sm text-muted-foreground py-1.5 ps-2">
                                  {item.label}
                                </Link>
                              ))}
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Mobile Silo 2: IT */}
                <div>
                  <button
                    onClick={() => setExpandedMobileSection(expandedMobileSection === 'it' ? null : 'it')}
                    className="flex items-center justify-between w-full py-3 text-foreground font-medium"
                  >
                    <span className="flex items-center gap-2">
                      <Code className="h-4 w-4 text-orange" />
                      {L.itSilo}
                    </span>
                    <ChevronRight className={`h-4 w-4 transition-transform ${expandedMobileSection === 'it' ? 'rotate-90' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {expandedMobileSection === 'it' && (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                        <div className="ps-4 pb-3 space-y-4">
                          {itColumns.map((col) => (
                            <div key={col.title}>
                              <div className={`text-xs font-semibold uppercase tracking-wider ${col.color} mb-2`}>{col.title}</div>
                              {col.items.map((item) => (
                                <Link key={item.href} to={`/${language}/services/digital-studio/${item.href}`} className="block text-sm text-muted-foreground py-1.5 ps-2">
                                  {item.label}
                                </Link>
                              ))}
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Mobile Silo 3: Industries */}
                <div>
                  <button
                    onClick={() => setExpandedMobileSection(expandedMobileSection === 'industries' ? null : 'industries')}
                    className="flex items-center justify-between w-full py-3 text-foreground font-medium"
                  >
                    <span className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-cyan" />
                      {L.industries}
                    </span>
                    <ChevronRight className={`h-4 w-4 transition-transform ${expandedMobileSection === 'industries' ? 'rotate-90' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {expandedMobileSection === 'industries' && (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                        <div className="ps-4 pb-3 grid grid-cols-2 gap-1">
                          {industriesList.map((ind) => (
                            <Link key={ind.href} to={`/${language}/industries/${ind.href}`} className="flex items-center gap-2 text-sm text-muted-foreground py-1.5">
                              <span>{ind.emoji}</span>
                              {ind.label}
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* ── MARKETPLACE ── */}
                <div>
                  <button
                    onClick={() => setExpandedMobileSection(expandedMobileSection === 'marketplace' ? null : 'marketplace')}
                    className="flex items-center justify-between w-full py-3 text-foreground font-medium"
                  >
                    <span className="flex items-center gap-2">
                      <Briefcase className="h-4 w-4 text-cyan" />
                      {language === 'fr' ? 'Marketplace' : language === 'es' ? 'Marketplace' : language === 'ar' ? 'السوق' : language === 'de' ? 'Marktplatz' : 'Marketplace'}
                    </span>
                    <ChevronRight className={`h-4 w-4 transition-transform ${expandedMobileSection === 'marketplace' ? 'rotate-90' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {expandedMobileSection === 'marketplace' && (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                        <div className="ps-4 pb-3 space-y-1">
                          <Link to={`/${language}/marketplace/freelancers`} onClick={() => setIsMobileMenuOpen(false)} className="block text-sm text-muted-foreground py-1.5 ps-2 hover:text-primary">
                            {language === 'fr' ? 'Trouver des Freelances' : language === 'es' ? 'Buscar Freelancers' : language === 'ar' ? 'ابحث عن مستقلين' : language === 'de' ? 'Freelancer finden' : 'Find Freelancers'}
                          </Link>
                          <Link to={`/${language}/marketplace/missions`} onClick={() => setIsMobileMenuOpen(false)} className="block text-sm text-muted-foreground py-1.5 ps-2 hover:text-primary">
                            {language === 'fr' ? 'Parcourir les Missions' : language === 'es' ? 'Ver Misiones' : language === 'ar' ? 'تصفح المهام' : language === 'de' ? 'Aufträge durchsuchen' : 'Browse Missions'}
                          </Link>
                          <Link to={`/${language}/contact`} onClick={() => setIsMobileMenuOpen(false)} className="block text-sm text-muted-foreground py-1.5 ps-2 hover:text-primary">
                            {language === 'fr' ? 'Publier une Mission' : language === 'es' ? 'Publicar Misión' : language === 'ar' ? 'نشر مهمة' : language === 'de' ? 'Auftrag erstellen' : 'Post a Mission'}
                          </Link>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* ── COMPANY ── */}
                {/* Home */}
                <Link to={`/${language}`} onClick={() => setIsMobileMenuOpen(false)} className="py-3 text-foreground font-medium">{L.home}</Link>
                {/* Services */}
                <Link to={`/${language}/services`} onClick={() => setIsMobileMenuOpen(false)} className="py-3 text-foreground font-medium">{L.services}</Link>
                {/* Simple links */}
                <Link to={`/${language}/pricing-models`} onClick={() => setIsMobileMenuOpen(false)} className="py-3 text-foreground font-medium">{L.pricing}</Link>
                <Link to={`/${language}/case-studies`} onClick={() => setIsMobileMenuOpen(false)} className="py-3 text-foreground font-medium">{L.caseStudies}</Link>
                <Link to={`/${language}/blog`} onClick={() => setIsMobileMenuOpen(false)} className="py-3 text-foreground font-medium">{L.blog}</Link>
                <Link to={`/${language}/about`} onClick={() => setIsMobileMenuOpen(false)} className="py-3 text-foreground font-medium">{L.about}</Link>
                <Link to={`/${language}/careers`} onClick={() => setIsMobileMenuOpen(false)} className="py-3 text-foreground font-medium">{L.careers}</Link>
                <Link to={`/${language}/contact`} onClick={() => setIsMobileMenuOpen(false)} className="py-3 text-foreground font-medium">{L.contact}</Link>
              </nav>

              {/* Mobile Language Switcher */}
              <div className="mt-6 pt-6 border-t border-border">
                <div className="flex flex-wrap gap-2">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => switchLanguage(lang.code)}
                      className={`px-3 py-2 rounded-lg text-lg transition-colors
                        ${language === lang.code ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-primary hover:bg-primary/5'}`}
                    >
                      {lang.flag}
                    </button>
                  ))}
                </div>
              </div>

              {/* Mobile Auth & CTA */}
              <div className="mt-6 pt-6 border-t border-border space-y-3">
                {user ? (
                  <Link to={getDashboardLink()} className="block">
                    <Button variant="outline" className="w-full border-primary text-primary hover:bg-primary/10">
                      <LayoutDashboard className="h-4 w-4 mr-2" />
                      {(t.nav as Record<string, string>)?.mySpace || 'Mon espace'}
                    </Button>
                  </Link>
                ) : (
                  <Link to={`/${language}/auth`} className="block">
                    <Button variant="outline" className="w-full border-cyan text-cyan hover:bg-cyan/10">
                      <LogIn className="h-4 w-4 mr-2" />
                      {t.nav?.login || 'Login'}
                    </Button>
                  </Link>
                )}
{!onOrderWizard && (
                <Button asChild className="w-full bg-gradient-to-r from-orange to-orange-light text-white font-semibold">
                  <Link to={headerQuoteHref}>{L.getQuote}</Link>
                </Button>
)}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}